import { Link } from "react-router-dom";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

const ELIGIBLE = ["Proprietorship Firms", "Partnership Firms", "LLPs", "Private Limited Companies", "Public Limited Companies", "Technical Service Organizations", "Textile Machinery Consultants"];

const BENEFITS = [
    "Official STMF Membership ID",
    "Digital Membership Certificate",
    "QR-Based Member Verification",
    "Access to Member Directory",
    "Participation in Exhibitions",
    "Networking Opportunities",
    "Business Referrals",
    "Technical Workshops",
    "Industry Updates & Circulars",
    "Representation Before Authorities",
];

const STEPS = [
    "Submit Online Application",
    "Upload Required Documents",
    "Verification by Membership Committee",
    "Executive Committee Approval",
    "Membership Fee Payment",
    "Membership ID Generation",
    "Certificate & Digital Membership Card Issued",
];

const SERVICES = [
    {
        title: "Digital Membership Card",
        desc: "Every approved member receives a digital membership card containing:",
        items: ["Member Name", "Company Name", "Membership ID", "QR Verification Code", "Membership Validity"],
    },
    {
        title: "QR Verification System",
        desc: "The STMF QR Verification System allows customers, banks, and institutions to verify member authenticity instantly. Verification displays:",
        items: ["Company Name", "Membership Status", "Membership Category", "Validity Period"],
    },
    {
        title: "Member Directory",
        desc: "Find verified suppliers and service providers by:",
        items: ["Company Name", "City", "Machinery Type", "Service Category", "Product Segment"],
    },
];

export default function Membership() {
    return (
        <div className="min-h-screen flex flex-col">
            <PublicHeader />

            {/* HERO */}
            <section className="bg-[#0F172A] text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Ordinary Membership</div>
                    <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight max-w-4xl">Open to individuals and business entities engaged in textile machinery activities.</h1>
                </div>
            </section>

            {/* ELIGIBLE + BENEFITS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-12">
                <div data-testid="eligible-block">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Eligible Organizations</div>
                    <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#0F172A] mb-8 leading-tight">Who can apply.</h2>
                    <ul className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0]">
                        {ELIGIBLE.map((e, i) => (
                            <li key={e} className="py-4 flex items-center gap-5">
                                <span className="font-mono text-xs text-[#D4AF37] w-8">{String(i + 1).padStart(2, "0")}</span>
                                <span className="text-[#0F172A] font-medium">{e}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div data-testid="benefits-block">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Membership Benefits</div>
                    <h2 className="font-display text-2xl lg:text-3xl font-bold text-[#0F172A] mb-8 leading-tight">What you receive.</h2>
                    <ul className="space-y-3">
                        {BENEFITS.map((b) => (
                            <li key={b} className="flex items-start gap-3 text-sm text-[#0F172A]">
                                <span className="text-[#16A34A] mt-0.5 font-bold">✓</span>
                                <span>{b}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* PROCESS */}
            <section className="bg-[#F8F9FA] border-y border-[#E2E8F0]" data-testid="process-block">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                    <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Membership Process</div>
                    <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#0F172A] mb-14 leading-tight max-w-3xl">Seven steps from application to active membership.</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                        {STEPS.map((s, i) => (
                            <div key={s} className="bg-white p-8" data-testid={`step-${i + 1}`}>
                                <div className="font-display text-4xl font-bold text-[#D4AF37] mb-3 tabular">{String(i + 1).padStart(2, "0")}</div>
                                <div className="text-xs uppercase tracking-[0.12em] text-[#64748B] mb-2">Step {i + 1}</div>
                                <div className="font-display font-bold text-[#0F172A] leading-snug">{s}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
                <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4">Member Services</div>
                <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#0F172A] mb-14 leading-tight max-w-3xl">Digital tools that work for verified members.</h2>
                <div className="grid md:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
                    {SERVICES.map((s, i) => (
                        <div key={s.title} className="bg-white p-8" data-testid={`service-${i}`}>
                            <div className="font-mono text-xs text-[#D4AF37] mb-3">0{i + 1}</div>
                            <h3 className="font-display text-xl font-bold text-[#0F172A] mb-3">{s.title}</h3>
                            <p className="text-sm text-[#64748B] mb-5 leading-relaxed">{s.desc}</p>
                            <ul className="space-y-1.5 text-sm text-[#0F172A]">
                                {s.items.map((it) => <li key={it} className="flex items-start gap-2"><span className="text-[#D4AF37]">•</span>{it}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            <section className="bg-[#0F172A] text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Start Your Application</div>
                        <h2 className="font-display text-3xl lg:text-4xl font-bold leading-tight">Ready to begin?</h2>
                    </div>
                    <Link to="/apply" data-testid="membership-cta-apply" className="inline-flex items-center gap-3 px-8 py-5 bg-[#D4AF37] text-[#0F172A] text-sm font-semibold uppercase tracking-[0.08em] hover:bg-[#B4952D] transition-colors">Apply Now <span>→</span></Link>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
